import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer,
  IconButton, List, ListItemButton, ListItemText, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import NearMeOutlinedIcon from "@mui/icons-material/NearMeOutlined";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import HorizontalRuleOutlinedIcon from "@mui/icons-material/HorizontalRuleOutlined";
import ArrowRightAltOutlinedIcon from "@mui/icons-material/ArrowRightAltOutlined";
import RectangleOutlinedIcon from "@mui/icons-material/RectangleOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import TitleOutlinedIcon from "@mui/icons-material/TitleOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import AutoFixNormalOutlinedIcon from "@mui/icons-material/AutoFixNormalOutlined";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import RedoOutlinedIcon from "@mui/icons-material/RedoOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import CenterFocusStrongOutlinedIcon from "@mui/icons-material/CenterFocusStrongOutlined";
import api from "../../api/client";
import { extractErrorMessage } from "../../utils/errorMessage";

const TOOLS = [
  { id: "select", label: "Select / Move (V)", icon: NearMeOutlinedIcon },
  { id: "pen", label: "Pen (P)", icon: CreateOutlinedIcon },
  { id: "line", label: "Line (L)", icon: HorizontalRuleOutlinedIcon },
  { id: "arrow", label: "Arrow (A)", icon: ArrowRightAltOutlinedIcon },
  { id: "rect", label: "Rectangle (R)", icon: RectangleOutlinedIcon },
  { id: "ellipse", label: "Ellipse (O)", icon: CircleOutlinedIcon },
  { id: "text", label: "Text (T)", icon: TitleOutlinedIcon },
  { id: "note", label: "Sticky note (N)", icon: StickyNote2OutlinedIcon },
  { id: "eraser", label: "Eraser (E)", icon: AutoFixNormalOutlinedIcon },
  { id: "pan", label: "Pan (hold Space)", icon: PanToolOutlinedIcon },
];
const COLORS = ["#1E1E1E", "#6C50BF", "#2E7DF7", "#12B886", "#F59F00", "#E8590C", "#E64980", "#FA5252", "#FFFFFF"];
const WIDTHS = [2, 4, 8];
const NOTE_FILL = "#FFF3BF";
const uid = () => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export default function Whiteboard() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const editorRef = useRef(null);
  const editorOpenAtRef = useRef(0);
  const pendingEditorRef = useRef(null);
  const [placementHint, setPlacementHint] = useState(null); // { x, y } in wrap (screen-relative) coords

  const [boards, setBoards] = useState([]);
  const [boardId, setBoardId] = useState(null);
  const [title, setTitle] = useState("Untitled board");
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#1E1E1E");
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [elements, setElements] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [view, setView] = useState({ scale: 1, tx: 0, ty: 0 });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { id, x, y, value, fontSize, isNote, w }
  const [shareOpen, setShareOpen] = useState(false);
  const [shareId, setShareId] = useState(null);
  const [linkAccess, setLinkAccess] = useState("none");
  const [myRole, setMyRole] = useState("owner"); // owner | editor | viewer
  const readOnly = myRole === "viewer";

  // Mutable mirrors so the imperative canvas renderer always sees fresh values.
  const elementsRef = useRef(elements);
  const viewRef = useRef(view);
  const toolRef = useRef(tool);
  const colorRef = useRef(color);
  const widthRef = useRef(strokeWidth);
  const selectedRef = useRef(selectedId);
  const draftRef = useRef(null);
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const pointerRef = useRef({ mode: null });
  const spaceRef = useRef(false);
  const lastPointerRef = useRef(null);
  const pointerInsideRef = useRef(false);
  const placementRafRef = useRef(null);
  const placementPendingRef = useRef(null);
  useEffect(() => { elementsRef.current = elements; }, [elements]);
  useEffect(() => { viewRef.current = view; }, [view]);
  useEffect(() => { toolRef.current = tool; }, [tool]);
  useEffect(() => { colorRef.current = color; }, [color]);
  useEffect(() => { widthRef.current = strokeWidth; }, [strokeWidth]);
  useEffect(() => { selectedRef.current = selectedId; }, [selectedId]);
  useEffect(() => {
    if (editing || readOnly) { setPlacementHint(null); return; }
    const isTextTool = tool === "text" || tool === "note";
    if (!isTextTool) { setPlacementHint(null); return; }
    if (!pointerInsideRef.current || !lastPointerRef.current) return;
    setPlacementHint(lastPointerRef.current);
  }, [tool, editing, readOnly]);

  /* ------------------------------- geometry ------------------------------- */
  const toWorld = (clientX, clientY) => {
    const r = canvasRef.current.getBoundingClientRect();
    const v = viewRef.current;
    return { x: (clientX - r.left - v.tx) / v.scale, y: (clientY - r.top - v.ty) / v.scale };
  };
  const bboxOf = (el) => {
    if (el.points) {
      const xs = el.points.map((p) => p.x), ys = el.points.map((p) => p.y);
      return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
    }
    const x = el.w < 0 ? el.x + el.w : el.x, y = el.h < 0 ? el.y + el.h : el.y;
    const w = Math.abs(el.w || 0) || (el.type === "text" ? Math.max(40, (el.text || "T").length * (el.fontSize || 24) * 0.5) : 0);
    const h = Math.abs(el.h || 0) || (el.type === "text" ? (el.fontSize || 24) * 1.3 : 0);
    return { x, y, w, h };
  };
  const hitTest = (pt) => {
    const els = elementsRef.current;
    for (let i = els.length - 1; i >= 0; i--) {
      const b = bboxOf(els[i]);
      const pad = 8 / viewRef.current.scale;
      if (pt.x >= b.x - pad && pt.x <= b.x + b.w + pad && pt.y >= b.y - pad && pt.y <= b.y + b.h + pad) return els[i].id;
    }
    return null;
  };

  /* ------------------------------- rendering ------------------------------ */
  const wrapText = (ctx, text, x, y, maxW, lh, fs, col) => {
    ctx.fillStyle = col; ctx.font = `${fs}px Inter, system-ui, sans-serif`; ctx.textBaseline = "alphabetic";
    String(text).split("\n").forEach((para) => {
      let line = "";
      para.split(" ").forEach((word) => {
        const test = line ? `${line} ${word}` : word;
        if (ctx.measureText(test).width > maxW && line) { ctx.fillText(line, x, y); y += lh; line = word; }
        else line = test;
      });
      ctx.fillText(line, x, y); y += lh;
    });
  };
  const drawEl = (ctx, el) => {
    ctx.strokeStyle = el.color || "#1E1E1E";
    ctx.lineWidth = el.strokeWidth || 4;
    ctx.lineJoin = "round"; ctx.lineCap = "round";
    if (el.type === "pen") {
      ctx.beginPath();
      el.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.stroke();
    } else if (el.type === "line" || el.type === "arrow") {
      const [a, b] = el.points;
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      if (el.type === "arrow") {
        const ang = Math.atan2(b.y - a.y, b.x - a.x), hh = 10 + (el.strokeWidth || 4) * 1.5;
        ctx.beginPath();
        ctx.moveTo(b.x, b.y); ctx.lineTo(b.x - hh * Math.cos(ang - 0.4), b.y - hh * Math.sin(ang - 0.4));
        ctx.moveTo(b.x, b.y); ctx.lineTo(b.x - hh * Math.cos(ang + 0.4), b.y - hh * Math.sin(ang + 0.4));
        ctx.stroke();
      }
    } else if (el.type === "rect") {
      ctx.strokeRect(el.x, el.y, el.w, el.h);
    } else if (el.type === "ellipse") {
      ctx.beginPath();
      ctx.ellipse(el.x + el.w / 2, el.y + el.h / 2, Math.abs(el.w / 2), Math.abs(el.h / 2), 0, 0, Math.PI * 2);
      ctx.stroke();
    } else if (el.type === "note") {
      ctx.fillStyle = el.fill || NOTE_FILL;
      ctx.shadowColor = "rgba(0,0,0,0.18)"; ctx.shadowBlur = 10; ctx.shadowOffsetY = 4;
      ctx.fillRect(el.x, el.y, el.w, el.h);
      ctx.shadowBlur = 0; ctx.shadowOffsetY = 0;
      wrapText(ctx, el.text || "", el.x + 12, el.y + 28, el.w - 24, (el.fontSize || 16) * 1.35, el.fontSize || 16, "#1E1E1E");
    } else if (el.type === "text") {
      wrapText(ctx, el.text || "", el.x, el.y + (el.fontSize || 24), el.maxW || 600, (el.fontSize || 24) * 1.3, el.fontSize || 24, el.color || "#1E1E1E");
    }
  };
  const render = useCallback(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.width / dpr, h = canvas.height / dpr;
    const v = viewRef.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    const gap = 24 * v.scale;
    if (gap > 9) {
      ctx.fillStyle = "rgba(0,0,0,0.09)";
      const ox = ((v.tx % gap) + gap) % gap, oy = ((v.ty % gap) + gap) % gap;
      for (let gx = ox; gx < w; gx += gap) for (let gy = oy; gy < h; gy += gap) { ctx.beginPath(); ctx.arc(gx, gy, 1, 0, Math.PI * 2); ctx.fill(); }
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.translate(v.tx, v.ty); ctx.scale(v.scale, v.scale);
    elementsRef.current.forEach((el) => drawEl(ctx, el));
    if (draftRef.current) drawEl(ctx, draftRef.current);
    const sel = elementsRef.current.find((e) => e.id === selectedRef.current);
    if (sel) {
      const b = bboxOf(sel);
      ctx.strokeStyle = "#6C50BF"; ctx.lineWidth = 1.5 / v.scale; ctx.setLineDash([6 / v.scale, 4 / v.scale]);
      ctx.strokeRect(b.x - 6, b.y - 6, b.w + 12, b.h + 12); ctx.setLineDash([]);
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current, wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ro = new ResizeObserver(() => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = wrap.clientWidth * dpr; canvas.height = wrap.clientHeight * dpr;
      canvas.style.width = `${wrap.clientWidth}px`; canvas.style.height = `${wrap.clientHeight}px`;
      render();
    });
    ro.observe(wrap);
    return () => ro.disconnect();
  }, [render]);
  useEffect(() => { render(); }, [elements, view, selectedId, render]);

  /* ------------------------------- history -------------------------------- */
  const commit = (next) => {
    pastRef.current.push(elementsRef.current);
    if (pastRef.current.length > 80) pastRef.current.shift();
    futureRef.current = [];
    setElements(next); setDirty(true);
  };
  const undo = () => {
    if (!pastRef.current.length) return;
    futureRef.current.push(elementsRef.current);
    setElements(pastRef.current.pop()); setDirty(true); setSelectedId(null);
  };
  const redo = () => {
    if (!futureRef.current.length) return;
    pastRef.current.push(elementsRef.current);
    setElements(futureRef.current.pop()); setDirty(true);
  };

  /* ---------------------------- pointer events ---------------------------- */
  const activeTool = () => (spaceRef.current ? "pan" : toolRef.current);
  const openEditor = (el) => {
    const v = viewRef.current;
    editorOpenAtRef.current = Date.now();
    setEditing({
      id: el.id, x: el.x * v.scale + v.tx, y: el.y * v.scale + v.ty,
      value: el.text || "", fontSize: (el.fontSize || 24) * v.scale, isNote: el.type === "note", w: (el.w || 320) * v.scale,
    });
  };
  useEffect(() => {
    if (!editing) return;
    const id = requestAnimationFrame(() => {
      const el = editorRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(el.value.length, el.value.length);
    });
    return () => cancelAnimationFrame(id);
  }, [editing?.id]);
  const onDown = (e) => {
    if (editing) return;
    canvasRef.current.setPointerCapture?.(e.pointerId);
    const p = toWorld(e.clientX, e.clientY);
    const t = readOnly ? "pan" : activeTool(); // viewers can pan/zoom but not edit
    pointerRef.current = { mode: t, start: p, moved: false, screen: { x: e.clientX, y: e.clientY }, view0: { ...viewRef.current } };
    if (t === "pan") return;
    if (t === "select") {
      const id = hitTest(p); setSelectedId(id);
      if (id) {
        pointerRef.current.dragEl = JSON.parse(JSON.stringify(elementsRef.current.find((x) => x.id === id)));
        pointerRef.current.base = elementsRef.current;
        const el = elementsRef.current.find((x) => x.id === id);
        if (el && (el.type === "text" || el.type === "note")) pointerRef.current.editOnUp = el;
      }
      return;
    }
    if (t === "eraser") { const id = hitTest(p); if (id) commit(elementsRef.current.filter((x) => x.id !== id)); return; }
    if (t === "note") {
      const note = { id: uid(), type: "note", x: p.x, y: p.y, w: 200, h: 150, text: "", fill: NOTE_FILL, fontSize: 16 };
      commit([...elementsRef.current, note]);
      pendingEditorRef.current = note;
      return;
    }
    if (t === "text") {
      const txt = { id: uid(), type: "text", x: p.x, y: p.y, text: "", color: colorRef.current, fontSize: 24, maxW: 600 };
      commit([...elementsRef.current, txt]);
      pendingEditorRef.current = txt;
      return;
    }
    if (t === "pen") draftRef.current = { id: uid(), type: "pen", points: [p], color: colorRef.current, strokeWidth: widthRef.current };
    else if (t === "line" || t === "arrow") draftRef.current = { id: uid(), type: t, points: [p, p], color: colorRef.current, strokeWidth: widthRef.current };
    else if (t === "rect" || t === "ellipse") draftRef.current = { id: uid(), type: t, x: p.x, y: p.y, w: 0, h: 0, color: colorRef.current, strokeWidth: widthRef.current };
    render();
  };
  const onMove = (e) => {
    // Placement hint for text/note tools even when not dragging.
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      lastPointerRef.current = { x, y };
      pointerInsideRef.current = true;
      const showHint = !editing && !readOnly && !spaceRef.current && (toolRef.current === "text" || toolRef.current === "note");
      if (showHint) {
        placementPendingRef.current = { x, y };
        if (!placementRafRef.current) {
          placementRafRef.current = requestAnimationFrame(() => {
            placementRafRef.current = null;
            if (placementPendingRef.current) setPlacementHint(placementPendingRef.current);
          });
        }
      }
    }
    const pr = pointerRef.current; if (!pr.mode) return;
    if (pr.mode === "pan") {
      const nv = { ...pr.view0, tx: pr.view0.tx + (e.clientX - pr.screen.x), ty: pr.view0.ty + (e.clientY - pr.screen.y) };
      viewRef.current = nv; setView(nv); return;
    }
    const p = toWorld(e.clientX, e.clientY); pr.moved = true;
    if (pr.mode === "select" && pr.dragEl) {
      const dx = p.x - pr.start.x, dy = p.y - pr.start.y;
      const el = JSON.parse(JSON.stringify(pr.dragEl));
      if (el.points) el.points = el.points.map((pt) => ({ x: pt.x + dx, y: pt.y + dy }));
      else { el.x += dx; el.y += dy; }
      elementsRef.current = pr.base.map((x) => (x.id === el.id ? el : x));
      render(); return;
    }
    const d = draftRef.current; if (!d) return;
    if (d.type === "pen") d.points.push(p);
    else if (d.type === "line" || d.type === "arrow") d.points[1] = p;
    else { d.w = p.x - d.x; d.h = p.y - d.y; }
    render();
  };
  const onUp = () => {
    const pr = pointerRef.current;
    if (pendingEditorRef.current) {
      openEditor(pendingEditorRef.current);
      pendingEditorRef.current = null;
    } else if (pr.mode === "select" && pr.editOnUp && !pr.moved && !readOnly) {
      openEditor(pr.editOnUp);
    }
    if (pr.mode === "select" && pr.dragEl && pr.moved) commit(elementsRef.current.slice());
    else if (draftRef.current) {
      const d = draftRef.current; draftRef.current = null;
      const dot = (d.type === "pen" && d.points.length < 2) || ((d.type === "rect" || d.type === "ellipse") && Math.abs(d.w) < 3 && Math.abs(d.h) < 3) || ((d.type === "line" || d.type === "arrow") && Math.hypot(d.points[1].x - d.points[0].x, d.points[1].y - d.points[0].y) < 3);
      if (!dot) commit([...elementsRef.current, d]);
    }
    pointerRef.current = { mode: null };
    render();
  };
  const onLeave = () => {
    pointerInsideRef.current = false;
    setPlacementHint(null);
    onUp();
  };
  const onWheel = (e) => {
    if (editing) return;
    e.preventDefault();
    const v = viewRef.current;
    const ns = clamp(v.scale * (e.deltaY < 0 ? 1.1 : 1 / 1.1), 0.15, 6);
    const r = canvasRef.current.getBoundingClientRect();
    const sx = e.clientX - r.left, sy = e.clientY - r.top;
    const wx = (sx - v.tx) / v.scale, wy = (sy - v.ty) / v.scale;
    const nv = { scale: ns, tx: sx - wx * ns, ty: sy - wy * ns };
    viewRef.current = nv; setView(nv);
  };

  /* ---------------------------- text editing ------------------------------ */
  const commitEditor = () => {
    if (!editing) return;
    const val = editing.value;
    if (Date.now() - editorOpenAtRef.current < 120 && !val.trim()) return;
    const next = elementsRef.current
      .map((el) => (el.id === editing.id ? { ...el, text: val } : el))
      .filter((el) => !(el.id === editing.id && el.type === "text" && !val.trim()));
    commit(next); setEditing(null);
  };

  /* ------------------------------ board CRUD ------------------------------ */
  const loadBoards = useCallback(async () => {
    try { const res = await api.get("/api/whiteboards"); setBoards(res.data.boards || []); }
    catch (err) { toast.error(extractErrorMessage(err)); }
  }, []);
  useEffect(() => { loadBoards(); }, [loadBoards]);

  const resetBoardState = (b, role = "owner") => {
    setBoardId(b?._id || null); setTitle(b?.title || "Untitled board"); setElements(b?.data?.elements || []);
    setShareId(b?.shareId || null); setLinkAccess(b?.linkAccess || "none"); setMyRole(role);
    pastRef.current = []; futureRef.current = []; setSelectedId(null); setDirty(false); setView({ scale: 1, tx: 0, ty: 0 });
  };
  const openBoard = async (id) => {
    setLoading(true); setDrawerOpen(false);
    try { const res = await api.get(`/api/whiteboards/${id}`); resetBoardState(res.data.board, res.data.myRole); }
    catch (err) { toast.error(extractErrorMessage(err)); } finally { setLoading(false); }
  };
  const openShared = useCallback(async (sid) => {
    setLoading(true);
    try { const res = await api.get(`/api/whiteboards/shared/${sid}`); resetBoardState(res.data.board, res.data.myRole); }
    catch (err) { toast.error(extractErrorMessage(err)); } finally { setLoading(false); }
  }, []);
  const newBoard = async () => {
    try { const res = await api.post("/api/whiteboards", { title: "Untitled board", data: { elements: [] } }); resetBoardState(res.data.board, "owner"); setDrawerOpen(false); loadBoards(); }
    catch (err) { toast.error(extractErrorMessage(err)); }
  };
  const save = useCallback(async (silent) => {
    if (saving || readOnly) return boardId;
    setSaving(true);
    try {
      let id = boardId;
      if (!boardId) { const res = await api.post("/api/whiteboards", { title, data: { elements: elementsRef.current } }); id = res.data.board._id; setBoardId(id); setShareId(res.data.board.shareId || null); }
      else await api.put(`/api/whiteboards/${boardId}`, { title, data: { elements: elementsRef.current } });
      setDirty(false); if (!silent) toast.success("Board saved"); loadBoards();
      return id;
    } catch (err) { toast.error(extractErrorMessage(err)); return boardId; } finally { setSaving(false); }
  }, [boardId, title, saving, readOnly, loadBoards]);

  // Open a board straight from a share link (?s=shareId) or ?board=id.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const s = params.get("s"), b = params.get("board");
    if (s) openShared(s);
    else if (b) openBoard(b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLink = async (access) => {
    let id = boardId; if (!id) id = await save(true);
    if (!id) return;
    try { const res = await api.put(`/api/whiteboards/${id}`, { linkAccess: access }); setLinkAccess(res.data.board.linkAccess); setShareId(res.data.board.shareId); }
    catch (err) { toast.error(extractErrorMessage(err)); }
  };
  const shareLink = shareId ? `${window.location.origin}/Whiteboard?s=${shareId}` : "";
  const copyLink = () => {
    if (!shareLink) return;
    navigator.clipboard?.writeText(shareLink).then(() => toast.success("Link copied")).catch(() => toast.info(shareLink));
  };
  const removeBoard = async (id) => {
    try { await api.delete(`/api/whiteboards/${id}`); if (id === boardId) resetBoardState(null); loadBoards(); toast.success("Board deleted"); }
    catch (err) { toast.error(extractErrorMessage(err)); }
  };
  useEffect(() => {
    if (!dirty || !boardId || readOnly) return;
    const t = setTimeout(() => save(true), 1800);
    return () => clearTimeout(t);
  }, [dirty, boardId, readOnly, elements, title, save]);

  /* ------------------------------ shortcuts ------------------------------- */
  useEffect(() => {
    const kd = (e) => {
      const typing = editing || ["INPUT", "TEXTAREA"].includes(e.target.tagName);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s") { e.preventDefault(); save(false); return; }
      if (typing) return;
      if (readOnly) { if (e.code === "Space") spaceRef.current = true; return; } // viewers: pan only
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); return; }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") { e.preventDefault(); redo(); return; }
      if (e.code === "Space") { spaceRef.current = true; return; }
      if ((e.key === "Delete" || e.key === "Backspace") && selectedRef.current) { commit(elementsRef.current.filter((x) => x.id !== selectedRef.current)); setSelectedId(null); return; }
      const map = { v: "select", p: "pen", l: "line", a: "arrow", r: "rect", o: "ellipse", t: "text", n: "note", e: "eraser" };
      if (map[e.key.toLowerCase()] && !e.metaKey && !e.ctrlKey) setTool(map[e.key.toLowerCase()]);
    };
    const ku = (e) => { if (e.code === "Space") spaceRef.current = false; };
    window.addEventListener("keydown", kd); window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, [editing, save, readOnly]);

  const exportPng = () => {
    const els = elementsRef.current; if (!els.length) return toast.info("Nothing to export yet");
    const bs = els.map(bboxOf);
    const minX = Math.min(...bs.map((b) => b.x)) - 40, minY = Math.min(...bs.map((b) => b.y)) - 40;
    const maxX = Math.max(...bs.map((b) => b.x + b.w)) + 40, maxY = Math.max(...bs.map((b) => b.y + b.h)) + 40;
    const w = Math.max(1, maxX - minX), h = Math.max(1, maxY - minY);
    const off = document.createElement("canvas"); off.width = w; off.height = h;
    const ctx = off.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h);
    ctx.translate(-minX, -minY); els.forEach((el) => drawEl(ctx, el));
    const a = document.createElement("a"); a.download = `${title || "whiteboard"}.png`; a.href = off.toDataURL("image/png"); a.click();
  };
  const resetView = () => setView({ scale: 1, tx: 0, ty: 0 });
  const PlacementIcon = tool === "note" ? StickyNote2OutlinedIcon : TitleOutlinedIcon;
  const cursor = useMemo(() => (spaceRef.current || tool === "pan" ? "grab" : tool === "select" ? "default" : tool === "eraser" ? "cell" : "crosshair"), [tool]);

  return (
    <Box sx={{ height: "calc(100vh - 64px)", display: "flex", flexDirection: "column", bgcolor: "#F7F7FB" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1, borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
        <Tooltip title="My boards"><IconButton onClick={() => setDrawerOpen(true)}><FolderOpenOutlinedIcon /></IconButton></Tooltip>
        <TextField variant="standard" value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }} placeholder="Untitled board"
          InputProps={{ disableUnderline: true, readOnly, sx: { fontWeight: 700, fontSize: 16 } }} sx={{ minWidth: 140, maxWidth: 340 }} />
        {readOnly ? <Chip size="small" icon={<VisibilityOutlinedIcon />} label="View only" variant="outlined" />
          : dirty ? <Chip size="small" label="Unsaved" color="warning" variant="outlined" />
          : boardId ? <Chip size="small" label="Saved" color="success" variant="outlined" /> : null}
        <Box sx={{ flex: 1 }} />
        {!readOnly && <Tooltip title="Undo (Ctrl+Z)"><span><IconButton size="small" onClick={undo} disabled={!pastRef.current.length}><UndoOutlinedIcon /></IconButton></span></Tooltip>}
        {!readOnly && <Tooltip title="Redo (Ctrl+Shift+Z)"><span><IconButton size="small" onClick={redo} disabled={!futureRef.current.length}><RedoOutlinedIcon /></IconButton></span></Tooltip>}
        <Tooltip title="Export PNG"><IconButton size="small" onClick={exportPng}><DownloadOutlinedIcon /></IconButton></Tooltip>
        {myRole !== "viewer" && <Tooltip title="Share board"><IconButton size="small" onClick={() => setShareOpen(true)}><ShareOutlinedIcon /></IconButton></Tooltip>}
        {!readOnly && <Button size="small" variant="contained" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon />} onClick={() => save(false)} disabled={saving}>Save</Button>}
      </Stack>

      <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
        <Stack sx={{ p: 1, gap: 0.5, borderRight: "1px solid", borderColor: "divider", bgcolor: "background.paper", overflowY: "auto" }}>
          {TOOLS.map((t) => {
            const Icon = t.icon; const on = tool === t.id;
            return (
              <Tooltip key={t.id} title={t.label} placement="right">
                <IconButton size="small" onClick={() => setTool(t.id)} sx={{ bgcolor: on ? "primary.main" : "transparent", color: on ? "#fff" : "text.secondary", "&:hover": { bgcolor: on ? "primary.dark" : "action.hover" }, borderRadius: 2 }}>
                  <Icon fontSize="small" />
                </IconButton>
              </Tooltip>
            );
          })}
          <Divider sx={{ my: 0.5 }} />
          {COLORS.map((c) => (
            <Box key={c} onClick={() => setColor(c)} sx={{ width: 26, height: 26, borderRadius: "50%", bgcolor: c, cursor: "pointer", mx: "auto", border: color === c ? "3px solid #6C50BF" : "1px solid rgba(0,0,0,0.2)" }} />
          ))}
          <Divider sx={{ my: 0.5 }} />
          {WIDTHS.map((wv) => (
            <IconButton key={wv} size="small" onClick={() => setStrokeWidth(wv)} sx={{ borderRadius: 2, bgcolor: strokeWidth === wv ? "action.selected" : "transparent" }}>
              <Box sx={{ width: 18, height: wv, borderRadius: 4, bgcolor: "text.primary" }} />
            </IconButton>
          ))}
        </Stack>

        <Box ref={wrapRef} sx={{ flex: 1, position: "relative", overflow: "hidden", minWidth: 0 }}>
          {loading && <Box sx={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", zIndex: 5 }}><CircularProgress /></Box>}
          <canvas ref={canvasRef} style={{ display: "block", touchAction: "none", cursor }}
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onLeave} onWheel={onWheel} />
          {editing && (
            <textarea ref={editorRef} autoFocus value={editing.value}
              onChange={(e) => setEditing((s) => ({ ...s, value: e.target.value }))}
              onBlur={commitEditor}
              onPointerDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === "Escape") { e.preventDefault(); commitEditor(); }
                else if (e.key === "Enter" && !e.shiftKey && !editing.isNote) { e.preventDefault(); commitEditor(); }
              }}
              style={{
                position: "absolute", left: editing.x, top: editing.y, width: editing.isNote ? editing.w - 24 : 340,
                minHeight: editing.fontSize * 1.4, background: editing.isNote ? "transparent" : "rgba(255,255,255,0.92)",
                border: "1px dashed #6C50BF", outline: "none", resize: "none", padding: editing.isNote ? "16px 12px" : 2,
                fontSize: editing.fontSize, lineHeight: 1.3, fontFamily: "Inter, system-ui, sans-serif", color: "#1E1E1E", zIndex: 6,
              }} />
          )}
          {!editing && !readOnly && placementHint && (tool === "text" || tool === "note") && (
            <Box sx={{
              position: "absolute",
              left: placementHint.x,
              top: placementHint.y,
              transform: "translate(-50%, -100%)",
              pointerEvents: "none",
              zIndex: 4,
            }}>
              <Box sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 999,
                bgcolor: "rgba(108,80,191,0.10)",
                border: "1px solid rgba(108,80,191,0.35)",
                backdropFilter: "blur(6px)",
                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
              }}>
                <NearMeOutlinedIcon sx={{ fontSize: 16, color: "#6C50BF" }} />
                <PlacementIcon sx={{ fontSize: 16, color: "#6C50BF" }} />
              </Box>
            </Box>
          )}
          <Stack direction="row" spacing={0.5} sx={{ position: "absolute", right: 12, bottom: 12, bgcolor: "background.paper", borderRadius: 2, boxShadow: 2, p: 0.5 }}>
            <IconButton size="small" onClick={() => setView((v) => ({ ...v, scale: clamp(v.scale / 1.15, 0.15, 6) }))}><RemoveIcon fontSize="small" /></IconButton>
            <Button size="small" onClick={resetView} sx={{ minWidth: 52 }}>{Math.round(view.scale * 100)}%</Button>
            <IconButton size="small" onClick={() => setView((v) => ({ ...v, scale: clamp(v.scale * 1.15, 0.15, 6) }))}><AddIcon fontSize="small" /></IconButton>
            <Tooltip title="Reset view"><IconButton size="small" onClick={resetView}><CenterFocusStrongOutlinedIcon fontSize="small" /></IconButton></Tooltip>
          </Stack>
          <Tooltip title="Clear board">
            <IconButton size="small" onClick={() => { if (elementsRef.current.length) { commit([]); setSelectedId(null); } }} sx={{ position: "absolute", left: 12, bottom: 12, bgcolor: "background.paper", boxShadow: 2 }}>
              <DeleteSweepOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 300, p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography sx={{ fontWeight: 800 }}>My Whiteboards</Typography>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={newBoard}>New</Button>
          </Stack>
          <Divider />
          <List dense>
            {boards.length === 0 && <Typography sx={{ p: 2, fontSize: 13, color: "text.secondary" }}>No boards yet — hit New to create one.</Typography>}
            {boards.map((b) => (
              <ListItemButton key={b._id} selected={b._id === boardId} onClick={() => openBoard(b._id)} sx={{ borderRadius: 2 }}>
                <ListItemText primary={b.title}
                  secondary={`${b.mine ? "Owner" : "Shared"} · ${new Date(b.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`}
                  primaryTypographyProps={{ fontSize: 14, fontWeight: 600, noWrap: true }} />
                {b.mine && <IconButton size="small" edge="end" onClick={(e) => { e.stopPropagation(); removeBoard(b._id); }}><DeleteOutlineOutlinedIcon fontSize="small" /></IconButton>}
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>

      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Share whiteboard</DialogTitle>
        <DialogContent>
          {myRole === "owner" ? (
            <Stack spacing={2} sx={{ pt: 0.5 }}>
              <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Anyone at InfyU with the link can:</Typography>
              <Stack direction="row" spacing={1}>
                {[["none", "Off"], ["view", "Can view"], ["edit", "Can edit"]].map(([v, l]) => (
                  <Button key={v} size="small" variant={linkAccess === v ? "contained" : "outlined"} onClick={() => setLink(v)}>{l}</Button>
                ))}
              </Stack>
              {linkAccess !== "none" && shareId && (
                <Stack direction="row" spacing={1}>
                  <TextField size="small" fullWidth value={shareLink} onFocus={(e) => e.target.select()} InputProps={{ readOnly: true, sx: { fontSize: 12 } }} />
                  <Button variant="contained" startIcon={<ContentCopyOutlinedIcon />} onClick={copyLink} sx={{ whiteSpace: "nowrap" }}>Copy</Button>
                </Stack>
              )}
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {linkAccess === "none" ? "Link sharing is off — only you can open this board." : "Only logged-in InfyU employees can open the link."}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              <Typography sx={{ fontSize: 14 }}>You have <b>{myRole === "editor" ? "edit" : "view"}</b> access to this shared board. Only the owner can change sharing.</Typography>
              {shareLink && (
                <Stack direction="row" spacing={1}>
                  <TextField size="small" fullWidth value={shareLink} InputProps={{ readOnly: true, sx: { fontSize: 12 } }} />
                  <Button variant="outlined" startIcon={<ContentCopyOutlinedIcon />} onClick={copyLink}>Copy</Button>
                </Stack>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setShareOpen(false)}>Done</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
